'use client';

import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { BookOpenIcon } from '@phosphor-icons/react/dist/csr/BookOpen';

import { useFocusTrap } from 'hooks/dom/useFocusTrap';

import ExplainersSidebar from './docs/Sidebar';
import ExplainerToc, { type TocItem } from './ExplainerToc';
import type { QueryBlock } from './search/conversation';
import type { ExplainerNavCluster } from './types';
import styles from './ExplainerShell.module.css';
import './tokens.css';

/* Loaded on first open; the panel renders nothing until then, and its corpus
   comes from /api/science/explainers-search rather than page props. */
const ExplainersChatPanel = dynamic(() => import('./search/SearchPanel'), {
  ssr: false,
});

export interface Crumb {
  label: string;
  href?: string;
}

interface Props {
  crumbs: Crumb[];
  nav: ExplainerNavCluster[];
  activeClusterKey: string;
  activeSlug?: string;
  toc?: TocItem[];
  /* A SERVER subtree. The article renders on the server and only its output
     crosses the boundary — never its inputs. */
  children: ReactNode;
}

/* Shared docs chrome for every explainer page — three columns
 * (sidebar | content | table-of-contents) with a hairline under the global
 * header and a vertical rule on the sidebar edge.
 *
 * Desktop ≥1180: all three columns.
 * Tablet 1024–1180: sidebar + content (TOC rail hidden; pages render an
 *   inline TOC in the content flow).
 * Mobile <1024: single column with a top "Contents" + search bar that opens
 *   the nav in a bottom sheet.
 *
 * Owns the search overlay, the ⌘K shortcut and the mobile sheet so individual
 * pages stay declarative. */
const ExplainerShell = ({
  crumbs,
  nav,
  activeClusterKey,
  activeSlug = '',
  toc = [],
  children,
}: Props) => {
  const t = useTranslations('science');
  const [searchOpen, setSearchOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [searchBlocks, setSearchBlocks] = useState<QueryBlock[]>([]);
  const [searchMounted, setSearchMounted] = useState(false);
  const openSearch = () => {
    setSearchMounted(true);
    setSearchOpen(true);
  };
  const sheetRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLElement>(null);

  useFocusTrap(sheetRef, {
    active: sheetOpen,
    onEscape: () => setSheetOpen(false),
  });

  /* JetBrains Mono is configured preload:false / display:swap, and the browser
     does not lazily fetch the woff2 on render — the mono UI (breadcrumb, meta
     line, READ NOW, ⌘K) paints in the Menlo fallback and never swaps. Force
     the faces to load on mount so they swap in to real JetBrains Mono. */
  useEffect(() => {
    if (typeof document === 'undefined' || !document.fonts) return;
    ['400', '500'].forEach((w) => {
      document.fonts.load(`${w} 1em "JetBrains Mono"`).catch(() => undefined);
    });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openSearch();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!sheetOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSheetOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [sheetOpen]);

  /* Where the bottom of the fixed global nav actually sits, written onto the
     shell as --uh-mobile-nav-h.
   *
   * The stylesheet's 46px and 70px were measured against a nav that starts at
   * the top of the viewport. It does not always: a campaign banner renders
   * above it, which pushed the nav's bottom edge to 77px and left the Contents
   * bar — pinned at 46px — drawn over the logo. Measuring removes the
   * assumption, so a banner appearing, changing height or being dismissed all
   * resolve on their own. The CSS constants stay as the pre-hydration
   * fallback. */
  useEffect(() => {
    const page = pageRef.current;
    const header = document.querySelector('header');
    if (!page || !header) return undefined;

    const apply = () => {
      /* +1 keeps the hairline gap the constants had, rather than butting the
         bar against the nav. */
      const bottom = Math.round(header.getBoundingClientRect().bottom) + 1;
      if (bottom > 1) page.style.setProperty('--uh-mobile-nav-h', `${bottom}px`);
    };
    apply();

    /* The header's own size is not what changes when a banner appears above
       it — its position does. So watch the container the two share, whose
       height does change, and watch for the banner being added or removed at
       all. Observing only the header would miss the case this exists for. */
    const ro = new ResizeObserver(apply);
    ro.observe(header);
    if (header.parentElement) ro.observe(header.parentElement);

    const mo = new MutationObserver(apply);
    if (header.parentElement) {
      mo.observe(header.parentElement, { childList: true, subtree: false });
    }

    window.addEventListener('resize', apply);
    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', apply);
    };
  }, []);

  const sidebar = (onNavigate?: () => void) => (
    <ExplainersSidebar
      nav={nav}
      activeClusterKey={activeClusterKey}
      activeSlug={activeSlug}
      onOpenSearch={() => openSearch()}
      onNavigate={onNavigate}
    />
  );

  return (
    <>
      <main ref={pageRef} className={`expScope ${styles.page}`}>
        <div className={styles.mobileBar}>
          <button
            type="button"
            className={styles.contentsBtn}
            onClick={() => setSheetOpen(true)}
            aria-expanded={sheetOpen}
            aria-haspopup="dialog"
          >
            <svg
              className={styles.barIcon}
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M3 6h18M3 12h18M3 18h12" />
            </svg>
            {t('science.explainers.nav.contents')}
            <span className={styles.caret} aria-hidden="true">
              ▾
            </span>
          </button>
          <button
            type="button"
            className={styles.mobileSearch}
            onClick={() => openSearch()}
            aria-label={t('science.explainers.hero.searchLabel')}
          >
            <svg
              className={styles.barIcon}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          </button>
        </div>

        <section className={styles.layout}>
          <div
            className={styles.layoutInner}
            data-has-toc={toc.length > 0 ? 'true' : undefined}
          >
            <aside className={styles.sidebarColumn}>
              <div className={styles.sidebarFloat}>{sidebar()}</div>
            </aside>

            <div className={styles.main}>
              {crumbs.length > 0 && (
                <nav
                  className={styles.crumbs}
                  aria-label={t('science.explainers.nav.breadcrumb')}
                >
                  {crumbs.map((c, i) => (
                    <Fragment key={`${c.label}-${i}`}>
                      {i > 0 && (
                        <span className={styles.sep} aria-hidden="true">
                          /
                        </span>
                      )}
                      {c.href ? (
                        <Link
                          className={styles.crumbLink}
                          href={c.href}
                          aria-label={c.label}
                        >
                          {/* The root "Explainers" crumb collapses to an icon
                              on mobile so the trail stays on one line. */}
                          {i === 0 && (
                            <span className={styles.crumbIcon} aria-hidden="true">
                              <BookOpenIcon weight="regular" size={13} />
                            </span>
                          )}
                          <span
                            className={styles.crumbText}
                            data-icon-at={i === 0 ? 'true' : undefined}
                          >
                            {c.label}
                          </span>
                        </Link>
                      ) : (
                        <span className={styles.crumbCurrent} aria-current="page">
                          {c.label}
                        </span>
                      )}
                    </Fragment>
                  ))}
                </nav>
              )}
              {children}
            </div>

            {toc.length > 0 && (
              <aside className={styles.tocColumn}>
                <div className={styles.tocFloat}>
                  <ExplainerToc toc={toc} />
                </div>
              </aside>
            )}
          </div>
        </section>
        <div className={styles.footerRule} />
      </main>

      {sheetOpen && (
        <div
          className={`expScope ${styles.sheetBackdrop}`}
          onClick={() => setSheetOpen(false)}
        >
          <div
            className={styles.sheet}
            ref={sheetRef}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={t('science.explainers.nav.explainersNav')}
          >
            <button
              type="button"
              className={styles.sheetGrip}
              onClick={() => setSheetOpen(false)}
              aria-label={t('science.common.close')}
            />
            <div className={styles.sheetBody}>
              {sidebar(() => setSheetOpen(false))}
            </div>
          </div>
        </div>
      )}

      {searchMounted ? (
        <ExplainersChatPanel
          open={searchOpen}
          blocks={searchBlocks}
          onBlocks={setSearchBlocks}
          onClose={() => setSearchOpen(false)}
        />
      ) : null}
    </>
  );
};

export default ExplainerShell;
